import { useCallback, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { AgentOptionItem } from '@Models';

const cardVariants = tv({
  slots: {
    root: 'mt-3 mb-1 flex flex-col gap-1',
    button:
      'group/mscard relative flex w-full items-center gap-3 rounded-lg border bg-[#fbfaf9] px-3 py-2.5 text-left transition-all duration-150 animate-journey-slide-up focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c6cf6]/40 focus-visible:ring-offset-1 active:scale-[.99]',
    box: 'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors',
    content: 'min-w-0 flex-1',
    label: 'text-sm font-semibold leading-snug text-gray-900',
    hint: 'mt-0.5 text-xs leading-snug text-gray-500',
    confirm:
      'mt-1 self-end rounded-lg bg-[#7c6cf6] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#6b5be0] disabled:cursor-not-allowed disabled:opacity-40',
  },
  variants: {
    state: {
      idle: {
        button: 'border-gray-100 cursor-pointer hover:border-[#bdacfa] hover:bg-[#f5f3ff]',
        box: 'border-gray-300 bg-white text-transparent',
      },
      checked: {
        button: 'border-[#7c6cf6] bg-[#f5f3ff] cursor-pointer',
        box: 'border-[#7c6cf6] bg-[#7c6cf6] text-white',
      },
      disabled: {
        button: 'border-gray-100 cursor-not-allowed opacity-40',
        box: 'border-gray-200 bg-gray-50 text-transparent',
      },
    },
  },
  defaultVariants: { state: 'idle' },
});

interface MultiSelectCardsProps {
  options: AgentOptionItem[];
  /** Persisted selection: when present the block is locked (already confirmed). */
  selectedIds?: string[];
  disabled?: boolean;
  onConfirm?: (selected: AgentOptionItem[]) => void;
}

/**
 * Multi-select variant of OptionCards: checkboxes + a "Confirm selection"
 * button. On confirm it hands the chosen items to the parent, which submits
 * their labels as one follow-up message (so the selection round-trips through
 * message text and persists — mirrors the single-select flow).
 */
const MultiSelectCards = ({ options, selectedIds, disabled = false, onConfirm }: MultiSelectCardsProps) => {
  const styles = cardVariants();
  const alreadyConfirmed = (selectedIds?.length ?? 0) > 0;
  const [checked, setChecked] = useState<Set<string>>(() => new Set(selectedIds ?? []));
  // Self-lock the instant the user confirms — prevents a double-submit in the
  // window before this turn stops being the latest (which is what otherwise
  // locks it, via `disabled`, once the agent's reply starts).
  const [submitted, setSubmitted] = useState(false);
  const locked = disabled || alreadyConfirmed || submitted;

  const toggle = useCallback(
    (id: string) => {
      if (locked) return;
      setChecked((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [locked],
  );

  const selected = useMemo(() => options.filter((o) => checked.has(o.id)), [options, checked]);

  if (!options || options.length === 0) return null;

  return (
    <div className={styles.root()} role="group" aria-label="Multi-select options">
      {options.map((option, idx) => {
        const isChecked = checked.has(option.id);
        let state: 'idle' | 'checked' | 'disabled' = 'idle';
        if (isChecked) state = 'checked';
        else if (locked) state = 'disabled';
        return (
          <button
            key={option.id}
            type="button"
            role="checkbox"
            aria-checked={isChecked}
            disabled={locked}
            onClick={() => toggle(option.id)}
            className={styles.button({ state })}
            style={{ animationDelay: `${idx * 0.08 + 0.05}s` }}>
            <div className={styles.box({ state })} aria-hidden>
              <Check className="h-3.5 w-3.5" />
            </div>
            <div className={styles.content()}>
              <div className={styles.label()}>{option.label}</div>
              {option.hint && <div className={styles.hint()}>{option.hint}</div>}
            </div>
          </button>
        );
      })}
      {!locked && (
        <button
          type="button"
          className={styles.confirm()}
          disabled={selected.length === 0}
          onClick={() => {
            setSubmitted(true);
            onConfirm?.(selected);
          }}>
          {selected.length > 0 ? `Confirm selection (${selected.length})` : 'Confirm selection'}
        </button>
      )}
    </div>
  );
};

export default MultiSelectCards;
